import { usePostHog } from 'posthog-js/react';
import { useCallback } from 'react';
import { telemetryEventName, type TelemetryModule } from '@erp/shared-utils';

export interface FlowTelemetry {
  /**
   * Emit a `module.flow.step` PostHog event, stamped with
   * `{ module, flow, step, ...properties }`.
   */
  track: (step: string, properties?: Record<string, unknown>) => void;
}

/**
 * Frontend half of the reusable flow-telemetry primitive (the backend half is
 * `trackFlowEvent`). Binds a `module` + `flow` and returns a
 * `track(step, properties)` that emits the canonical `module.flow.step` event.
 *
 * Null-safe: when PostHog hasn't loaded (or is disabled) the call is a no-op,
 * so callers never need to guard. New flows reuse this instead of calling
 * `posthog.capture` with ad-hoc event names.
 */
export function useFlowTelemetry(
  module: TelemetryModule | string,
  flow: string,
): FlowTelemetry {
  const posthog = usePostHog();
  const track = useCallback(
    (step: string, properties?: Record<string, unknown>) => {
      posthog?.capture(telemetryEventName(module, flow, step), {
        ...properties,
        module,
        flow,
        step,
      });
    },
    [posthog, module, flow],
  );
  return { track };
}
